/** @format */

import { CloseOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { Alert, Button, Checkbox, DatePicker, Input, Segmented, Select, Spin, Tooltip } from "antd";
import moment, { Moment } from "moment";
import React, { useEffect, useMemo, useState } from "react";
import {
  TElementoDaGestire,
  TPersonaPresenze,
  TPresenzeDaGestire,
} from "../../../../redux-modules/declarations/maps/presenzeVista";
import {
  AZIONI_APPROVA,
  AZIONI_RIFIUTA,
  azioniSegnalazione,
  chipSegnalazione,
  CODICI,
  corrispondeNome,
  ESITI_TIMEBANK,
  etichettaGiorno,
  nomeCompleto,
  plurale,
  TContestoSegnalazione,
  tipoSegnalazione,
  titoloSegnalazione,
  TTipoSegnalazione,
  TUnitaPresenze,
  uscitaProposta,
} from "../format";
import { BarraSelezione, BarraStrumenti, ListaDaGestire, Stato } from "../styles";
import { azioniPresenze, TEsitoAzione, TLettura } from "../usePresenze";
import { PulsantiAzione, TRisorseAzioni } from "./Azioni";
import { DettaglioSegnalazione } from "./DettaglioSegnalazione";
import { AvatarPersona } from "./AvatarPersona";
import { chiaveId } from "./Giornata";

export type TTipoPeriodo = "oggi" | "ieri" | "sette" | "mese" | "scelto";

export interface TPeriodo {
  tipo: TTipoPeriodo;
  dal: string;
  al: string;
}

export const GIORNI_MASSIMI = 93;

/** Date del periodo, dal giorno di oggi del browser. */
export const periodoDa = (
  tipo: TTipoPeriodo,
  oggi: Moment,
  scelto?: [string, string],
): TPeriodo => {
  const f = (m: Moment): string => m.format("YYYY-MM-DD");
  switch (tipo) {
    case "oggi":
      return { tipo, dal: f(oggi), al: f(oggi) };
    case "ieri": {
      const ieri = oggi.clone().subtract(1, "day");
      return { tipo, dal: f(ieri), al: f(ieri) };
    }
    case "mese":
      return { tipo, dal: f(oggi.clone().startOf("month")), al: f(oggi) };
    case "scelto":
      return scelto ? { tipo, dal: scelto[0], al: scelto[1] } : periodoDa("sette", oggi);
    default:
      return { tipo: "sette", dal: f(oggi.clone().subtract(6, "days")), al: f(oggi) };
  }
};

interface TEsitoRiga {
  ok: boolean;
  testo: string;
  elemento: TElementoDaGestire;
}

interface Props {
  lettura: TLettura<TPresenzeDaGestire>;
  periodo: TPeriodo;
  unita: TUnitaPresenze;
  gruppi: string[];
  reparti: string[];
  risorse: Omit<TRisorseAzioni, "onEsito" | "permessi" | "tipoPresenzaOrdinariaId" | "modalita">;
  onPeriodo: (periodo: TPeriodo) => void;
  onPreferenze: (modifica: { gruppi?: string[]; reparti?: string[] }) => void;
  onModificato: () => void;
  onApriGiornata: (persona: TPersonaPresenze, data: string) => void;
}

const contesto = (e: TElementoDaGestire): TContestoSegnalazione => ({
  chiave: e.chiave,
  codice: e.codice,
  livello: e.livello,
  data: e.data,
  descrizione: e.descrizione,
  dettagli: e.dettagli || {},
  persona: e.persona,
  presenza: e.presenza,
  turno: e.turno,
  bloccato: e.bloccato,
});

const TIPI: { valore: TTipoSegnalazione; etichetta: (straordinario: boolean) => string }[] = [
  { valore: "tutte", etichetta: (): string => "Tutte" },
  {
    valore: "timebank",
    etichetta: (s: boolean): string =>
      s ? "Straordinari e turni parziali" : "Banca ore da approvare",
  },
  { valore: "mancate", etichetta: (): string => "Mancate timbrature" },
  { valore: "chiusura", etichetta: (): string => "Chiusure e presenze lunghe" },
  { valore: "senza_turno", etichetta: (): string => "Senza turno" },
];

export const DaGestire = ({
  lettura,
  periodo,
  unita,
  gruppi,
  reparti,
  risorse,
  onPeriodo,
  onPreferenze,
  onModificato,
  onApriGiornata,
}: Props): JSX.Element => {
  const { dati, caricamento, aggiornamento, errore, ricarica } = lettura;
  const [livello, setLivello] = useState<"anomalia" | "avviso">("anomalia");
  const [tipo, setTipo] = useState<TTipoSegnalazione>("tutte");
  const [ricerca, setRicerca] = useState("");
  const [selezione, setSelezione] = useState<string[]>([]);
  const [esiti, setEsiti] = useState<Record<string, TEsitoRiga>>({});
  const [messaggio, setMessaggio] = useState<string | null>(null);
  const [inBlocco, setInBlocco] = useState<{ fatte: number; totale: number } | null>(null);

  // cambiando periodo gli esiti delle righe gestite non servono più
  useEffect(() => {
    setEsiti({});
    setSelezione([]);
    setMessaggio(null);
  }, [periodo.dal, periodo.al]);

  const modalita = dati?.modalita_presenze || "banca_ore";
  const permessi = dati?.permessi;
  const oggi = dati?.periodo.oggi || moment().format("YYYY-MM-DD");

  const opzioniGruppi = useMemo(() => {
    const visti: Record<string, string> = {};
    (dati?.elementi || []).forEach(e => {
      visti[chiaveId(e.persona.gruppo_id)] = e.persona.gruppo || "Senza gruppo";
    });
    gruppi.forEach(g => {
      if (!visti[g]) visti[g] = "Gruppo senza segnalazioni";
    });
    return Object.keys(visti)
      .map(k => ({ value: k, label: visti[k] }))
      .sort((a, b) => a.label.localeCompare(b.label, "it"));
  }, [dati, gruppi]);
  const opzioniReparti = useMemo(() => {
    const visti: Record<string, string> = {};
    (dati?.elementi || []).forEach(e => {
      visti[chiaveId(e.persona.reparto_id)] = e.persona.reparto || "Senza reparto";
    });
    reparti.forEach(r => {
      if (!visti[r]) visti[r] = "Reparto senza segnalazioni";
    });
    return Object.keys(visti)
      .map(k => ({ value: k, label: visti[k] }))
      .sort((a, b) => a.label.localeCompare(b.label, "it"));
  }, [dati, reparti]);

  const nelPerimetro = (e: TElementoDaGestire): boolean =>
    (gruppi.length === 0 || gruppi.indexOf(chiaveId(e.persona.gruppo_id)) >= 0) &&
    (reparti.length === 0 || reparti.indexOf(chiaveId(e.persona.reparto_id)) >= 0) &&
    corrispondeNome(e.persona, ricerca);

  const base = (dati?.elementi || []).filter(nelPerimetro);
  const conteggioLivello = {
    anomalia: base.filter(e => e.livello === "anomalia").length,
    avviso: base.filter(e => e.livello === "avviso").length,
  };
  const conteggioTipo = (t: TTipoSegnalazione): number =>
    base.filter(
      e => e.livello === "anomalia" && (t === "tutte" || tipoSegnalazione(e.codice) === t),
    ).length;

  const delLivello = (e: TElementoDaGestire): boolean =>
    e.livello === livello &&
    (livello === "avviso" || tipo === "tutte" || tipoSegnalazione(e.codice) === tipo);

  // righe gestite in questa visita: restano in elenco con l'esito finché non si cambia periodo
  const righe = ((): TElementoDaGestire[] => {
    const presenti: Record<string, boolean> = {};
    const elenco = base.filter(delLivello);
    elenco.forEach(e => {
      presenti[e.chiave] = true;
    });
    Object.keys(esiti).forEach(k => {
      const e = esiti[k].elemento;
      if (!presenti[k] && esiti[k].ok && nelPerimetro(e) && delLivello(e)) elenco.push(e);
    });
    return elenco.sort(
      (a, b) =>
        b.data.localeCompare(a.data) ||
        nomeCompleto(a.persona).localeCompare(nomeCompleto(b.persona), "it") ||
        a.chiave.localeCompare(b.chiave),
    );
  })();

  const fatta = (e: TElementoDaGestire): boolean => !!esiti[e.chiave]?.ok;
  const selezionabili = righe.filter(e => e.in_blocco && !fatta(e) && livello === "anomalia");
  const tutteSelezionate =
    selezionabili.length > 0 && selezionabili.every(e => selezione.indexOf(e.chiave) >= 0);

  const giorni: { data: string; righe: TElementoDaGestire[] }[] = [];
  righe.forEach(e => {
    const ultimo = giorni[giorni.length - 1];
    if (ultimo && ultimo.data === e.data) ultimo.righe.push(e);
    else giorni.push({ data: e.data, righe: [e] });
  });

  const registra = (e: TElementoDaGestire, esito: TEsitoAzione): void => {
    setEsiti(attuali => ({
      ...attuali,
      [e.chiave]: { ok: esito.ok, testo: esito.messaggio, elemento: e },
    }));
    setSelezione(attuale => attuale.filter(k => k !== e.chiave));
  };

  const eseguiInBlocco = async (approva: boolean): Promise<void> => {
    const scelte = righe.filter(e => selezione.indexOf(e.chiave) >= 0 && !fatta(e));
    if (!scelte.length) return;
    setInBlocco({ fatte: 0, totale: scelte.length });
    let riuscite = 0;
    // una alla volta: l'esito compare su ogni riga
    for (let i = 0; i < scelte.length; i++) {
      const e = scelte[i];
      const azioni = e.presenza?.timebank?.azioni || [];
      const codice = azioni.find(a => (approva ? AZIONI_APPROVA : AZIONI_RIFIUTA).indexOf(a) >= 0);
      let esito: TEsitoAzione;
      if (!e.presenza || !codice) {
        esito = { ok: false, messaggio: "Azione non disponibile su questa timbratura" };
      } else {
        // eslint-disable-next-line no-await-in-loop
        esito = await azioniPresenze.timebank(e.presenza, codice, true);
        if (esito.ok) esito = { ok: true, messaggio: ESITI_TIMEBANK[codice] };
      }
      if (esito.ok) riuscite++;
      registra(e, esito);
      setInBlocco({ fatte: i + 1, totale: scelte.length });
    }
    setInBlocco(null);
    setSelezione([]);
    const non = scelte.length - riuscite;
    setMessaggio(
      `${plurale(
        riuscite,
        approva ? "timbratura approvata" : "timbratura rifiutata",
        approva ? "timbrature approvate" : "timbrature rifiutate",
      )}` +
        (non ? `, ${plurale(non, "non riuscita", "non riuscite")}: il motivo è sulla riga.` : "."),
    );
    onModificato();
  };

  const scegliPeriodo = (tipo_: TTipoPeriodo): void => onPeriodo(periodoDa(tipo_, moment()));

  const straordinario = modalita === "straordinario";

  return (
    <>
      <BarraStrumenti>
        <div className="riga">
          <span className="mostra">Periodo</span>
          <Segmented
            value={periodo.tipo === "scelto" ? "" : periodo.tipo}
            onChange={(v): void => scegliPeriodo(v as TTipoPeriodo)}
            options={[
              { label: "Oggi", value: "oggi" },
              { label: "Ieri", value: "ieri" },
              { label: "Ultimi 7 giorni", value: "sette" },
              { label: "Questo mese", value: "mese" },
            ]}
          />
          <DatePicker.RangePicker
            allowClear={false}
            format="DD/MM/YYYY"
            value={[moment(periodo.dal), moment(periodo.al)]}
            disabledDate={(d): boolean => d.isAfter(moment(), "day")}
            onChange={(v): void => {
              if (!v || !v[0] || !v[1]) return;
              let [dal, al] = [v[0], v[1]];
              if (al.diff(dal, "days") + 1 > GIORNI_MASSIMI) {
                dal = al.clone().subtract(GIORNI_MASSIMI - 1, "days");
              }
              onPeriodo({
                tipo: "scelto",
                dal: dal.format("YYYY-MM-DD"),
                al: al.format("YYYY-MM-DD"),
              });
            }}
            aria-label="Dal giorno al giorno"
          />
          <Select
            className="filtro"
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            placeholder="Tutti i gruppi"
            aria-label="Gruppi"
            value={gruppi}
            options={opzioniGruppi}
            optionFilterProp="label"
            onChange={(v: string[]): void => onPreferenze({ gruppi: v })}
          />
          <Select
            className="filtro"
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            placeholder="Tutti i reparti"
            aria-label="Reparti"
            value={reparti}
            options={opzioniReparti}
            optionFilterProp="label"
            onChange={(v: string[]): void => onPreferenze({ reparti: v })}
          />
          <Input
            className="ricerca"
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Cerca dipendente"
            aria-label="Cerca dipendente"
            value={ricerca}
            onChange={(e): void => setRicerca(e.target.value)}
          />
        </div>
        <div className="riga">
          <Segmented
            aria-label="Livello"
            value={livello}
            onChange={(v): void => {
              setLivello(v as "anomalia" | "avviso");
              setSelezione([]);
            }}
            options={[
              {
                value: "anomalia",
                label: (
                  <span>
                    Anomalie<span className="conteggio rosso">{conteggioLivello.anomalia}</span>
                  </span>
                ),
              },
              {
                value: "avviso",
                label: (
                  <span>
                    Avvisi<span className="conteggio ambra">{conteggioLivello.avviso}</span>
                  </span>
                ),
              },
            ]}
          />
          {livello === "anomalia" && (
            <>
              <span className="mostra">Tipo</span>
              <Segmented
                value={tipo}
                onChange={(v): void => {
                  setTipo(v as TTipoSegnalazione);
                  setSelezione([]);
                }}
                options={TIPI.map(t => ({
                  value: t.valore,
                  label: (
                    <span>
                      {t.etichetta(straordinario)}
                      <span className="conteggio">{conteggioTipo(t.valore)}</span>
                    </span>
                  ),
                }))}
              />
            </>
          )}
          <div className="spazio" />
          {aggiornamento && <Spin size="small" />}
          {dati && (
            <Stato>
              Dal {moment(dati.periodo.dal).format("D MMMM")} al{" "}
              {moment(dati.periodo.al).format("D MMMM YYYY")}
            </Stato>
          )}
        </div>
      </BarraStrumenti>

      {errore && (
        <Alert
          type="error"
          showIcon
          message={errore}
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={ricarica}>
              Riprova
            </Button>
          }
        />
      )}

      {messaggio && (
        <Alert
          type="success"
          showIcon
          message={messaggio}
          action={
            <Button
              size="small"
              type="text"
              icon={<CloseOutlined />}
              aria-label="Chiudi il messaggio"
              onClick={(): void => setMessaggio(null)}
            />
          }
        />
      )}

      {(selezione.length > 0 || inBlocco) && permessi && (
        <BarraSelezione role="region" aria-label="Selezione">
          <strong>
            {inBlocco
              ? `Operazione ${inBlocco.fatte} di ${inBlocco.totale}`
              : plurale(selezione.length, "selezionata", "selezionate")}
          </strong>
          <Button
            ghost
            size="small"
            disabled={!!inBlocco}
            onClick={(): Promise<void> => eseguiInBlocco(true)}
          >
            {straordinario ? "Approva gli straordinari selezionati" : "Approva le selezionate"}
          </Button>
          <Button
            ghost
            size="small"
            disabled={!!inBlocco}
            onClick={(): Promise<void> => eseguiInBlocco(false)}
          >
            Rifiuta le selezionate
          </Button>
          <Button
            type="text"
            size="small"
            style={{ color: "#fff" }}
            disabled={!!inBlocco}
            onClick={(): void => setSelezione([])}
          >
            Annulla selezione
          </Button>
          <span className="nota">
            Le operazioni partono una alla volta e l’esito compare su ogni riga.
          </span>
        </BarraSelezione>
      )}

      <Spin spinning={caricamento}>
        <ListaDaGestire>
          <div className="elenco">
            <div className="riga testata">
              <div className="scelta">
                {livello === "anomalia" && permessi?.in_blocco && (
                  <Checkbox
                    aria-label="Seleziona tutte le timbrature da approvare in elenco"
                    checked={tutteSelezionate}
                    indeterminate={!tutteSelezionate && selezione.length > 0}
                    disabled={!selezionabili.length || !!inBlocco}
                    onChange={(): void =>
                      setSelezione(tutteSelezionate ? [] : selezionabili.map(e => e.chiave))
                    }
                  />
                )}
              </div>
              <div>Persona</div>
              <div>Segnalazione</div>
              <div>Dettaglio</div>
              <div style={{ textAlign: "right" }}>Azione</div>
            </div>

            {giorni.map(g => (
              <React.Fragment key={g.data}>
                <div className="giorno">
                  {etichettaGiorno(g.data, oggi)}
                  <span className="pillola grigio">
                    {plurale(g.righe.filter(e => !fatta(e)).length, "da gestire", "da gestire")}
                  </span>
                </div>
                {g.righe.map(e => (
                  <RigaDaGestire
                    key={e.chiave}
                    elemento={e}
                    esito={esiti[e.chiave] || null}
                    selezionata={selezione.indexOf(e.chiave) >= 0}
                    selezionabile={livello === "anomalia" && e.in_blocco && !fatta(e) && !inBlocco}
                    unita={unita}
                    dati={dati}
                    risorse={risorse}
                    onSeleziona={(si): void =>
                      setSelezione(attuale =>
                        si ? [...attuale, e.chiave] : attuale.filter(k => k !== e.chiave),
                      )
                    }
                    onEsito={(esito): void => {
                      registra(e, esito);
                      if (esito.ok) onModificato();
                    }}
                    onApriGiornata={onApriGiornata}
                  />
                ))}
              </React.Fragment>
            ))}

            {dati && !righe.length && (
              <div className="vuoto">
                <strong>
                  {livello === "anomalia"
                    ? "Nessuna anomalia da gestire con questi filtri."
                    : "Nessun avviso con questi filtri."}
                </strong>
                <span className="attenuato">Cambia periodo o tipo, oppure svuota la ricerca.</span>
              </div>
            )}
          </div>
        </ListaDaGestire>
      </Spin>
    </>
  );
};

interface PropsRiga {
  elemento: TElementoDaGestire;
  esito: TEsitoRiga | null;
  selezionata: boolean;
  selezionabile: boolean;
  unita: TUnitaPresenze;
  dati: TPresenzeDaGestire | null;
  risorse: Omit<TRisorseAzioni, "onEsito" | "permessi" | "tipoPresenzaOrdinariaId" | "modalita">;
  onSeleziona: (si: boolean) => void;
  onEsito: (esito: TEsitoAzione) => void;
  onApriGiornata: (persona: TPersonaPresenze, data: string) => void;
}

const RigaDaGestire = ({
  elemento: e,
  esito,
  selezionata,
  selezionabile,
  unita,
  dati,
  risorse,
  onSeleziona,
  onEsito,
  onApriGiornata,
}: PropsRiga): JSX.Element => {
  const c = contesto(e);
  const modalita = dati?.modalita_presenze || "banca_ore";
  const fatta = !!esito?.ok;
  const permessi = dati?.permessi;
  const { pulsanti, nota } = permessi
    ? azioniSegnalazione(c, permessi)
    : { pulsanti: [], nota: null };
  const nome = nomeCompleto(e.persona);
  const daSceglierePerRiga =
    e.codice === CODICI.TIMEBANK &&
    !e.in_blocco &&
    !fatta &&
    (e.presenza?.timebank?.azioni.length || 0) > 0;

  return (
    <div className={`riga elemento${fatta ? " fatto" : ""}`}>
      <div className="scelta">
        {selezionabile && (
          <Checkbox
            aria-label={`Seleziona ${nome}, ${titoloSegnalazione(c, modalita).toLowerCase()}`}
            checked={selezionata}
            onChange={(ev): void => onSeleziona(ev.target.checked)}
          />
        )}
        {!selezionabile && daSceglierePerRiga && (
          <Tooltip title="Serve una scelta che si fa dalla riga">
            <span>
              <Checkbox disabled aria-label="Da gestire dalla riga, serve una scelta" />
            </span>
          </Tooltip>
        )}
      </div>
      <div className="chi">
        <AvatarPersona persona={e.persona} />
        <span className="testi">
          <span className="nome">{nome}</span>
          <span className="gruppo">
            {[e.persona.gruppo, e.persona.reparto].filter(Boolean).join(", ")}
          </span>
        </span>
      </div>
      <div>
        <span
          className={`chip ${fatta ? "verde" : e.livello === "anomalia" ? "rosso" : "ambra"}`}
          title={titoloSegnalazione(c, modalita)}
        >
          {fatta ? "Gestita" : chipSegnalazione(c, modalita, unita)}
        </span>
      </div>
      <div className="dettaglio">
        <DettaglioSegnalazione contesto={c} modalita={modalita} unita={unita} />
        {!fatta && nota && <span className="nota">{nota}</span>}
      </div>
      <div className="azioni">
        {esito && <span className={`esito ${esito.ok ? "ok" : "ko"}`}>{esito.testo}</span>}
        {!fatta && permessi && dati && (
          <PulsantiAzione
            pulsanti={pulsanti}
            risorse={{
              ...risorse,
              modalita,
              permessi,
              tipoPresenzaOrdinariaId: dati.tipo_presenza_ordinaria_id,
              onEsito,
            }}
            persona={e.persona}
            presenza={e.presenza}
            turno={e.turno}
            giorno={e.data}
            uscitaProposta={uscitaProposta(c)}
          />
        )}
        <Button size="small" type="link" onClick={(): void => onApriGiornata(e.persona, e.data)}>
          Apri giornata
        </Button>
      </div>
    </div>
  );
};
